import React from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import PageHeader from '../components/PageHeader';
import CompanyLogos from '../components/CompanyLogos';
import Team from '../components/Team';
import { stats, trustBadges, founder, founders } from '../mock';
import { ArrowUpRight, BadgeCheck, Award, ShieldCheck, TrendingUp, Linkedin, MapPin } from 'lucide-react';

const iconMap = { BadgeCheck, Award, ShieldCheck, TrendingUp };

const About = () => {
  return (
    <div className="font-body bg-paper text-[#1c1c1c]">
      <Navbar />
      <PageHeader
        eyebrow="About Eduvantage"
        title={<>Building India’s next <em className="italic">tax leaders</em></>}
        description="We’re on a mission to make US taxation the fastest, most affordable path to a global career for Indian professionals."
        breadcrumb="About"
      />

      {/* Story */}
      <section className="py-16 md:py-20">
        <div className="max-w-6xl mx-auto px-6 grid grid-cols-1 lg:grid-cols-2 gap-10 md:gap-14 items-center">
          <div>
            <h2 className="font-serif-display text-[#143324] text-4xl md:text-5xl leading-tight mb-6">
              Our <em className="italic">Story</em>
            </h2>
            <div className="space-y-4 text-[#3d3d3d] font-body leading-relaxed text-[15px]">
              <p>
                Eduvantage Solutions was founded in May 2022 by <span className="text-[#143324] font-medium">Koushik Rahul S</span> and <span className="text-[#143324] font-medium">Sijo Jacob Joseph</span> with a simple belief: an Indian graduate should be able to build a US-scale career without leaving home. Between Bengaluru and Austin, they saw thousands of talented CAs, MBAs, and commerce graduates lose out on US tax opportunities — not for lack of ability, but for lack of a structured path.
              </p>
              <p>
                So they built one. Our Enrolled Agent (EA) program bridges the gap between Indian talent and US tax demand — with IRS-aligned curriculum, live simulations on Big 14 software, and direct placement pipelines through the ProStrings network.
              </p>
              <p>
                Today, 200+ students have completed our programs, hold IRS licenses, and work with firms across the US, UK, and India — many as independent practitioners running their own six-figure firms from home.
              </p>
            </div>
            <div className="mt-8 flex items-center gap-3 flex-wrap">
              <Link to="/contact" className="inline-flex items-center gap-2 btn-primary px-6 py-3 rounded-full text-sm font-body">
                Get in Touch <ArrowUpRight size={16} />
              </Link>
              <Link to="/ea-course" className="inline-flex items-center gap-2 btn-outline px-6 py-3 rounded-full text-sm font-body">
                See Our Course
              </Link>
            </div>
          </div>

          <div className="card-soft rounded-2xl p-6 md:p-8">
            <p className="font-serif-display text-[#143324] text-2xl md:text-3xl leading-snug italic mb-6">
              “{founder.quote}”
            </p>
            <div className="flex items-center gap-3">
              <img src={founder.image} alt="founder" className="w-14 h-14 rounded-full object-cover" />
              <div>
                <p className="font-body font-semibold text-[#143324]">Sijo Jacob Joseph</p>
                <p className="text-[13px] text-[#3d3d3d] font-body">Co-Founder, Eduvantage {'·'} CEO, ProStrings</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Meet the Founders */}
      <section className="py-16 md:py-24 bg-paper-soft relative">
        <div className="squiggle-bg" />
        <div className="max-w-6xl mx-auto px-6 relative">
          <div className="mb-10 md:mb-14">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[#143324]/15 bg-white/60 text-[11px] tracking-[0.18em] uppercase text-[#143324] font-body mb-4">
              Meet the Founders
            </div>
            <h2 className="font-serif-display text-[#143324] text-4xl md:text-5xl leading-tight max-w-3xl">
              Two founders. <em className="italic">One mission.</em>
            </h2>
            <p className="text-[#3d3d3d] font-body text-[15px] mt-3 max-w-2xl">
              An India-and-US partnership uniting on-ground training with real US practice.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-8">
            {founders.map((f, i) => (
              <div key={i} className="card-soft rounded-3xl overflow-hidden flex flex-col" data-testid={`founder-card-${i}`}>
                <div className="aspect-[4/3] overflow-hidden bg-[#e8e2d1]">
                  <img
                    src={f.image}
                    alt={f.name}
                    className={`w-full h-full object-cover ${f.name.includes('Koushik') ? 'object-[center_18%] scale-110' : 'object-[center_20%]'}`}
                  />
                </div>
                <div className="p-6 md:p-8 flex-1 flex flex-col">
                  <div className="flex items-center gap-2 text-[11px] tracking-wider uppercase text-[#3d3d3d] font-body mb-2">
                    <MapPin size={12} className="text-[#143324]" /> {f.location}
                  </div>
                  <h3 className="font-serif-display text-[#143324] text-3xl md:text-4xl leading-tight mb-1">{f.name}</h3>
                  <p className="text-[13px] text-[#3d3d3d] font-body mb-1">{f.role}</p>
                  <p className="text-[12.5px] text-[#143324] font-body italic mb-4">{f.tagline}</p>

                  <p className="text-[14px] text-[#3d3d3d] leading-relaxed font-body mb-5">{f.bio}</p>

                  <ul className="space-y-2 mb-6">
                    {f.highlights.map((h, j) => (
                      <li key={j} className="flex items-start gap-2.5 text-[13px] text-[#1c1c1c] font-body">
                        <span className="mt-0.5 w-4 h-4 rounded-full bg-[#143324] text-[#f5f1e6] flex items-center justify-center shrink-0 text-[9px]">
                          {'?'}
                        </span>
                        {h}
                      </li>
                    ))}
                  </ul>

                  <div className="mt-auto pt-4 border-t border-[#143324]/10">
                    <a
                      href={f.linkedin}
                      target="_blank"
                      rel="noreferrer"
                      data-testid={`founder-linkedin-${i}`}
                      className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#0a66c2] text-white text-[13px] font-body hover:bg-[#004182] transition-colors"
                    >
                      <Linkedin size={15} strokeWidth={1.9} /> Connect on LinkedIn
                      <ArrowUpRight size={13} />
                    </a>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats + Mission */}
      <section className="py-16 md:py-20 bg-paper-soft">
        <div className="max-w-6xl mx-auto px-6">
          <div className="card-soft rounded-2xl p-8 md:p-12">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-4 text-center">
              {stats.map((s, i) => (
                <div key={i} className={`px-4 ${i < stats.length - 1 ? 'md:border-r border-[#143324]/10' : ''}`}>
                  <div className="font-serif-display text-[#143324] text-5xl md:text-6xl leading-none mb-2">{s.value}</div>
                  <p className="text-[13px] text-[#3d3d3d] font-body">{s.label}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
            <div className="card-soft rounded-2xl p-6 md:p-7">
              <h4 className="font-body font-semibold text-[#143324] text-lg mb-2">Our Mission</h4>
              <p className="text-[14px] text-[#3d3d3d] font-body leading-relaxed">Make US taxation the fastest, most affordable, and most respected career path for Indian graduates.</p>
            </div>
            <div className="card-soft rounded-2xl p-6 md:p-7">
              <h4 className="font-body font-semibold text-[#143324] text-lg mb-2">Our Approach</h4>
              <p className="text-[14px] text-[#3d3d3d] font-body leading-relaxed">IRS-aligned curriculum + Big 14 software + live tax return practice + guaranteed placement.</p>
            </div>
            <div className="card-soft rounded-2xl p-6 md:p-7">
              <h4 className="font-body font-semibold text-[#143324] text-lg mb-2">Our Promise</h4>
              <p className="text-[14px] text-[#3d3d3d] font-body leading-relaxed">A federally-licensed career in 6 months — or personalized mentoring until you cross the finish line.</p>
            </div>
          </div>

          <div className="mt-14 border-t border-[#143324]/10 pt-8">
            <div className="flex flex-wrap items-center justify-center gap-x-10 gap-y-4">
              {trustBadges.map((b, i) => {
                const Icon = iconMap[b.icon];
                return (
                  <div key={i} className="flex items-center gap-2 text-[#1c1c1c]">
                    {Icon && <Icon size={18} className="text-[#143324]" strokeWidth={1.6} />}
                    <span className="text-sm font-body">{b.label}</span>
                    {i < trustBadges.length - 1 && <span className="hidden md:inline text-[#143324]/25 ml-6">|</span>}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      <Team />
      <CompanyLogos />
      <Footer />
    </div>
  );
};

export default About;
